import React, { useCallback, useEffect } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { Button } from "./button";
import { cn } from "../../lib/utils";

export type LightboxImage = {
  src: string;
  alt: string;
};

type ImageLightboxProps = {
  images: LightboxImage[];
  currentIndex: number;
  onClose: () => void;
  onNavigate: (index: number) => void;
  imageClassName?: string;
};

export const ImageLightbox: React.FC<ImageLightboxProps> = ({
  images,
  currentIndex,
  onClose,
  onNavigate,
  imageClassName,
}) => {
  const goToPrev = useCallback(() => {
    onNavigate(currentIndex === 0 ? images.length - 1 : currentIndex - 1);
  }, [currentIndex, images.length, onNavigate]);

  const goToNext = useCallback(() => {
    onNavigate(currentIndex === images.length - 1 ? 0 : currentIndex + 1);
  }, [currentIndex, images.length, onNavigate]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") goToPrev();
      if (e.key === "ArrowRight") goToNext();
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose, goToPrev, goToNext]);

  const image = images[currentIndex];
  if (!image) return null;

  return (
    <div className="ds-overlay-backdrop fixed inset-0 z-50 flex flex-col items-center justify-center backdrop-blur-md">
      <Button
        type="button"
        variant="outline"
        onClick={onClose}
        className="absolute right-4 top-4 z-10 rounded-full px-4 py-2"
      >
        <X className="h-5 w-5" />
        Close
      </Button>

      <div className="flex items-center justify-center">
        {images.length > 1 && (
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={goToPrev}
            className="mx-4 shrink-0 rounded-full p-3"
          >
            <ChevronLeft className="h-8 w-8" />
          </Button>
        )}

        <div className="flex max-h-[75vh] max-w-[80vw] items-center justify-center">
          <img
            src={image.src}
            alt={image.alt}
            className={cn(
              "max-h-[75vh] max-w-[80vw] object-contain shadow-2xl",
              imageClassName,
            )}
          />
        </div>

        {images.length > 1 && (
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={goToNext}
            className="mx-4 shrink-0 rounded-full p-3"
          >
            <ChevronRight className="h-8 w-8" />
          </Button>
        )}
      </div>

      {images.length > 1 && (
        <div className="mt-6 flex h-12 w-12 items-center justify-center rounded-full border border-border bg-card text-sm font-medium text-foreground shadow-sm">
          {currentIndex + 1}/{images.length}
        </div>
      )}
    </div>
  );
};
